import React from 'react'
import classNames from 'classnames'
import CheckRoundIcon from '@rsuite/icons/CheckRound'
import WarningRoundIcon from '@rsuite/icons/WarningRound'
import { Remittance } from '../types'
import { formatAmount, formatDate, formatDateTime } from '../utils'

type Props = { remittance: Remittance }

const TOLERANCE = 0.01

/**
 * The cleaned-up remittance that was saved and delivered, with a check that
 * its line items add up to the payment total.
 */
const RemittanceCard: React.FC<Props> = ({ remittance }) => {
  const { totalAmount, lineItemsTotal } = remittance
  const comparable = totalAmount != null && lineItemsTotal != null
  const difference = comparable ? (totalAmount ?? 0) - (lineItemsTotal ?? 0) : 0
  const balanced = comparable && Math.abs(difference) < TOLERANCE

  const facts: [string, React.ReactNode][] = [
    ['Supplier', remittance.supplier],
    ['Supplier no.', remittance.supplierNo],
    ['Payer', remittance.payer],
    ['Payer ABN', remittance.payerAbn],
    ['Payment reference', remittance.paymentReference],
    [
      'Payment date',
      remittance.paymentDate && formatDate(`${remittance.paymentDate}T00:00:00`)
    ],
    ['Bank', remittance.supplierBankName]
  ]

  return (
    <div className="dx-remittance">
      {comparable && (
        <div
          className={classNames('dx-check', {
            'dx-check--ok': balanced,
            'dx-check--warn': !balanced
          })}
        >
          {balanced ? <CheckRoundIcon /> : <WarningRoundIcon />}
          <div>
            <p className="dx-check__title">
              {balanced
                ? 'Line items match the payment total'
                : 'Line items don’t add up to the payment total'}
            </p>
            <p className="dx-check__body">
              Total {formatAmount(totalAmount)} · Line items{' '}
              {formatAmount(lineItemsTotal)}
              {!balanced && ` · Difference ${formatAmount(difference)}`}
            </p>
          </div>
        </div>
      )}

      <dl className="dx-fields">
        {facts
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label} className="dx-field">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>

      {remittance.lineItems.length > 0 && (
        <div className="dx-table-wrap dx-table-wrap--inner">
          <table className="dx-table dx-table--compact">
            <thead>
              <tr>
                <th scope="col">Invoice no.</th>
                <th scope="col">Invoice date</th>
                <th scope="col" className="dx-num">
                  Gross
                </th>
                <th scope="col" className="dx-num">
                  Paid
                </th>
              </tr>
            </thead>
            <tbody>
              {remittance.lineItems.map((line, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`${line.invoiceNumber}-${i}`}>
                  <td className="dx-mono">{line.invoiceNumber || '—'}</td>
                  <td className="dx-nowrap">
                    {line.invoiceDate
                      ? formatDate(`${line.invoiceDate}T00:00:00`)
                      : '—'}
                  </td>
                  <td className="dx-num dx-nowrap">
                    {formatAmount(line.grossAmount)}
                  </td>
                  <td className="dx-num dx-nowrap">
                    {formatAmount(line.paidAmount)}
                  </td>
                </tr>
              ))}
            </tbody>
            {comparable && (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={3}>
                    Payment total
                  </th>
                  <td className="dx-num dx-nowrap dx-strong">
                    {formatAmount(totalAmount)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {remittance.processedAt && (
        <p className="dx-muted dx-small">
          Saved {formatDateTime(remittance.processedAt)}
        </p>
      )}
    </div>
  )
}

export default RemittanceCard
